import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { getActiveCities, CityFeatures } from '../services/firebase/cityServices';

// Firebase tipine göre güncellenen City tipi
export interface City {
  id: string;
  countryCode: string;
  countryName: string;
  cityCode: string;
  cityName: string;
  isActive: boolean;
  apiEndpoint: string;
  features: CityFeatures;
  routePlanning: boolean;
  announcements: boolean;
  location?: {
    latitude?: number;  // undefined olabilir
    longitude?: number; // undefined olabilir
  };
}

interface CityContextType {
  cities: City[];
  currentCity: City | null;
  setCurrentCity: (city: City) => void;
  showCitySelector: boolean;
  setShowCitySelector: (show: boolean) => void;
  isFirstVisit: boolean;
  setIsFirstVisit: (isFirst: boolean) => void;
  loading: boolean;
}

const CityContext = createContext<CityContextType | undefined>(undefined);

export const CityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState<City[]>([]);
  const [currentCity, setCurrentCity] = useState<City | null>(null);
  const [showCitySelector, setShowCitySelector] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  // Firebase'den aktif şehirleri yükle
  useEffect(() => {
    const loadCities = async () => {
      try {
        setLoading(true);
        const activeCities = await getActiveCities();
        
        // Firebase formatını uygulama formatına dönüştür
        const formattedCities: City[] = activeCities.map(fbCity => ({
          id: fbCity.id || '', 
          countryCode: fbCity.countryCode,
          countryName: fbCity.countryName,
          cityCode: fbCity.cityCode,
          cityName: fbCity.cityName,
          isActive: fbCity.isActive,
          apiEndpoint: fbCity.apiEndpoint,
          features: fbCity.features,
          routePlanning: fbCity.routePlanning,
          announcements: fbCity.announcements,
          location: fbCity.location ? {
            latitude: fbCity.location.latitude,
            longitude: fbCity.location.longitude
          } : undefined
        }));
        
        setCities(formattedCities);
        
        // Şehir yoksa işlemi sonlandır
        if (formattedCities.length === 0) {
          setLoading(false);
          return;
        }
        
        // Local storage'dan kaydedilmiş şehri al
        const savedCity = localStorage.getItem('selectedCity');
        if (savedCity) {
          try {
            const parsedCity = JSON.parse(savedCity);
            
            // Kaydedilmiş şehri güncel verilerle doğrula
            const matchedCity = formattedCities.find(c => c.cityCode === parsedCity.cityCode);
            
            if (matchedCity) {
              setCurrentCity(matchedCity);
            } else {
              // Eşleşme bulunamazsa ilk şehri seç
              setCurrentCity(formattedCities[0]);
              localStorage.setItem('selectedCity', JSON.stringify(formattedCities[0]));
            }
          } catch (e) {
            console.error("Kaydedilmiş şehir verisi okunamadı:", e);
            setCurrentCity(formattedCities[0]);
            localStorage.setItem('selectedCity', JSON.stringify(formattedCities[0]));
          }
        } else {
          // Kullanıcı ilk kez ziyaret ediyorsa veya kayıtlı şehir yoksa
          setIsFirstVisit(true);
          setShowCitySelector(true);
          setCurrentCity(formattedCities[0]);
        }
      } catch (error) {
        console.error('Şehirler yüklenirken hata oluştu:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadCities();
  }, []);
  
  // Şehir seçildiğinde local storage'a kaydet
  const handleCityChange = (city: City) => {
    setCurrentCity(city);
    localStorage.setItem('selectedCity', JSON.stringify(city));
    
    // Şehir seçilince isFirstVisit'i false olarak işaretleyelim
    if (isFirstVisit) {
      setIsFirstVisit(false);
    }
  };
  
  // Context değerlerini memoize ederek gereksiz yeniden render'ların önüne geçelim
  const contextValue = useMemo(() => ({
    cities,
    currentCity,
    setCurrentCity: handleCityChange,
    showCitySelector,
    setShowCitySelector,
    isFirstVisit,
    setIsFirstVisit,
    loading
  }), [cities, currentCity, showCitySelector, isFirstVisit, loading]);
  
  return (
    <CityContext.Provider value={contextValue}>
      {children}
    </CityContext.Provider>
  );
};

export const useCity = () => {
  const context = useContext(CityContext);
  if (context === undefined) {
    throw new Error('useCity must be used within a CityProvider');
  }
  return context;
};
